import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';

import { useCompany, useCompanyId } from './useCompany';
import { ApiError } from '../../api/problem';
import { github } from '../../api/projects';
import { Check, Copy } from 'lucide-react';
import { m } from 'motion/react';

import { Button } from '../../components/Button';
import { ErrorBanner } from '../../components/ErrorBanner';
import { SkeletonRows } from '../../components/Skeleton';
import { PageHeader } from '../../components/Surface';
import { useFormat } from '../../i18n/format';
import { useI18n, useT } from '../../i18n/provider';
import { useSubmit } from '../auth/useSubmit';

/**
 * `08 §2`: how this company reaches GitHub, in the three shapes it can take.
 *
 * The shape is not a preference, it is a **security property**, and the screen
 * is built around saying so:
 *
 * - **App installed** — a token can be cut down to what the employee running is
 *   allowed to do (`05 §4.1`), which is the second of the two levels the
 *   permission engine works at and the one that actually protects.
 * - **PAT** — it works, and every agent gets the same wide credential. That gets
 *   a **permanent** banner, not a dismissible notice: it is a standing property
 *   of the deployment and a manager who dismissed it once would never be told.
 * - **Nothing** — no repository can be connected at all, so the screen says what
 *   to do next instead of reporting an error.
 *
 * `GET /companies/{id}/github/repositories` is what tells them apart: it answers
 * `mode` and `trimmed` under either credential, and 422 when there is none.
 */
export function GithubSettingsScreen() {
  const t = useT();
  const companyId = useCompanyId();
  const company = useCompany();
  const queryClient = useQueryClient();
  const search = useSearch({ strict: false }) as { installation_id?: string; state?: string };

  const access = useQuery({
    queryKey: ['github-access', companyId],
    queryFn: () => github.repositories(companyId),
    enabled: companyId !== '',
    retry: false,
  });

  const configured = access.data !== undefined;
  const noCredential = access.error instanceof ApiError && access.error.status === 422;

  if (company.isPending || access.isPending) {
    return <SkeletonRows rows={3} />;
  }

  return (
    <section className="max-w-2xl space-y-8">
      <PageHeader title={t('githubSettings.title')} description={t('githubSettings.intro')} />

      {search.installation_id && search.state ? (
        <InstallationCallback
          companyId={companyId}
          installationId={Number(search.installation_id)}
          state={search.state}
        />
      ) : null}

      {access.error && !noCredential ? <ErrorBanner error={access.error} onRetry={() => void access.refetch()} /> : null}

      {noCredential ? <NothingConfigured companyId={companyId} /> : null}

      {configured && access.data.mode === 'github_app' ? <AppConnected count={access.data.data.length} /> : null}
      {configured && access.data.mode === 'github_pat' ? <PatConnected companyId={companyId} /> : null}

      <WebhookSecretPanel
        companyId={companyId}
        configured={company.data?.settings.github_webhook_secret_configured === true}
        onRotated={() => void queryClient.invalidateQueries({ queryKey: ['company', companyId] })}
      />
    </section>
  );
}

function AppConnected({ count }: { count: number }) {
  const t = useT();
  const { plural } = useI18n();
  const format = useFormat();

  return (
    <m.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-2 rounded-lg border border-success-line bg-success-pale p-4"
    >
      <p className="text-sm font-semibold text-ink">{t('githubSettings.appConnected')}</p>
      <p className="text-sm leading-relaxed text-ink-soft">
        {t('githubSettings.appExplanation')
          .split('{capability}')
          .flatMap((part, index) => [
            part,
            index === 0 ? (
              <code key="capability" className="font-mono">
                pr.merge
              </code>
            ) : null,
          ])}
      </p>
      <p className="text-sm text-ink-soft">
        {count === 0
          ? t('githubSettings.noRepositories')
          : plural('githubSettings.visibleRepositories', count, { count: format.number(count) })}
      </p>
    </m.div>
  );
}

/**
 * The permanent banner of `08 §2.2`, and the reason this screen exists as its
 * own page rather than a row inside Credentials.
 */
function PatConnected({ companyId }: { companyId: string }) {
  const t = useT();

  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-warning-line bg-warning-pale p-4">
        <p className="text-sm font-semibold text-ink">{t('githubSettings.patTitle')}</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm leading-relaxed text-ink-soft">
          <li>{t('githubSettings.patPoints.trim')}</li>
          <li>{t('githubSettings.patPoints.webhooks')}</li>
          <li>{t('githubSettings.patPoints.protection')}</li>
        </ul>
      </div>

      <InstallAppButton companyId={companyId} label={t('githubSettings.installInstead')} />
    </div>
  );
}

function NothingConfigured({ companyId }: { companyId: string }) {
  const t = useT();

  return (
    <div className="space-y-3 rounded-lg border border-dashed border-line p-6">
      <p className="text-sm font-semibold text-ink">{t('githubSettings.nothingTitle')}</p>
      <p className="text-sm leading-relaxed text-ink-soft">{t('githubSettings.nothingHint')}</p>
      <InstallAppButton companyId={companyId} label={t('githubSettings.install')} />
    </div>
  );
}

/**
 * `GET /github/install-url` and the redirect to GitHub.
 *
 * The state it returns lives ten minutes and is what ties the installation back
 * to this company; sending the manager off without one would leave the callback
 * with nothing to verify. A deployment with no App registered answers 422, and
 * that message is worth showing verbatim — it names the three environment
 * variables somebody has to set.
 */
function InstallAppButton({ companyId, label }: { companyId: string; label: string }) {
  const { busy, error, submit } = useSubmit<{ url: string }>();

  return (
    <div className="space-y-2">
      <Button
        busy={busy}
        onClick={() =>
          void submit(
            () => github.installUrl(companyId),
            (result) => {
              window.location.assign(result.url);
            },
          )
        }
      >
        {label}
      </Button>
      {error ? <ErrorBanner error={error} /> : null}
    </div>
  );
}

/**
 * What GitHub redirects back to, relayed to the API.
 *
 * It runs once and only once: a second POST of the same `installation_id` is
 * harmless — the endpoint reactivates rather than duplicating — but re-running
 * on every render would hammer it, and React 19 in strict mode mounts effects
 * twice. The ref is what makes «once» true in both.
 */
function InstallationCallback({
  companyId,
  installationId,
  state,
}: {
  companyId: string;
  installationId: number;
  state: string;
}) {
  const t = useT();
  const queryClient = useQueryClient();
  const { error, submit } = useSubmit<unknown>();
  const [done, setDone] = useState(false);
  const fired = useRef(false);

  useEffect(() => {
    if (fired.current || !Number.isFinite(installationId) || installationId <= 0) {
      return;
    }

    fired.current = true;

    void submit(
      () => github.install(companyId, { installation_id: installationId, state }),
      async () => {
        setDone(true);
        await queryClient.invalidateQueries({ queryKey: ['github-access', companyId] });
      },
    );
    // Deliberately not depending on `submit`: it is recreated every render, and
    // depending on it would re-fire the effect on each one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, installationId, state]);

  if (error) {
    return <ErrorBanner error={error} />;
  }

  return done ? (
    <m.p
      role="status"
      initial={{ opacity: 0, y: -6 }}
      animate={{ opacity: 1, y: 0 }}
      className="rounded-sm border border-success-line bg-success-pale px-4 py-2.5 text-sm text-ink"
    >
      {t('githubSettings.installed')}
    </m.p>
  ) : (
    <p role="status" className="flex items-center gap-2 text-sm text-ink-soft">
      <span className="animate-turn size-3 rounded-full border-2 border-line border-t-primary" aria-hidden="true" />
      {t('githubSettings.finishing')}
    </p>
  );
}

/**
 * The webhook URL and secret of `08 §2.2`.
 *
 * The secret used to be shown once and never again, which is the correct rule
 * for a credential this deployment holds alone and the wrong one here: GitHub
 * holds the same plaintext in every repository the hook is registered on, and
 * repositories get connected weeks after the secret is minted. So the value is
 * **always readable** — losing it used to mean rotating a working secret and
 * re-registering every repository, a bigger and riskier operation than reading
 * one.
 *
 * Readable, but not *ambient*: it arrives from a request of its own that the
 * server audits, never as a field of the company, so opening this screen does
 * not put it on the wire. That is why there is a "reveal" button at all rather
 * than the value simply being rendered.
 */
function WebhookSecretPanel({
  companyId,
  configured,
  onRotated,
}: {
  companyId: string;
  configured: boolean;
  onRotated: () => void;
}) {
  const t = useT();
  const rotation = useSubmit<{ url: string; secret: string; content_type: string }>();
  const reveal = useSubmit<{ url: string; secret: string | null; content_type: string }>();
  const [shown, setShown] = useState<{ url: string; secret: string } | null>(null);

  return (
    <div className="space-y-3 rounded-lg border border-line bg-raised p-5">
      <div>
        <h3 className="text-base font-semibold text-ink">{t('githubSettings.webhook')}</h3>
        <p className="text-sm leading-relaxed text-ink-soft">{t('githubSettings.webhookIntro')}</p>
      </div>

      {rotation.error ? <ErrorBanner error={rotation.error} /> : null}
      {reveal.error ? <ErrorBanner error={reveal.error} /> : null}

      {shown ? (
        <m.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-3 rounded-sm border border-line bg-muted p-3.5"
        >
          <CopyableValue label={t('githubSettings.payloadUrl')} value={shown.url} />
          <CopyableValue label={t('githubSettings.secretLabel')} value={shown.secret} />
          <p className="text-xs leading-relaxed text-ink-soft">
            {t('githubSettings.contentType')
              .split('{type}')
              .flatMap((part, index) => [
                part,
                index === 0 ? (
                  <span key="type" className="font-mono">
                    application/json
                  </span>
                ) : null,
              ])}
          </p>
        </m.div>
      ) : (
        <p className="text-sm text-ink-soft">
          {configured ? t('githubSettings.secretConfigured') : t('githubSettings.noSecret')}
        </p>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {configured && !shown ? (
          <Button
            busy={reveal.busy}
            onClick={() =>
              void reveal.submit(
                () => github.revealWebhookSecret(companyId),
                (result) => {
                  // `secret: null` means the company row lost it between the
                  // listing and this click — rare, but rendering an empty code
                  // block would read as "the secret is blank".
                  if (result.secret !== null) {
                    setShown({ url: result.url, secret: result.secret });
                  }
                },
              )
            }
          >
            {t('githubSettings.reveal')}
          </Button>
        ) : null}

        <Button
          variant={configured ? 'secondary' : 'primary'}
          busy={rotation.busy}
          onClick={() =>
            void rotation.submit(
              () => github.webhookSecret(companyId),
              (result) => {
                setShown({ url: result.url, secret: result.secret });
                onRotated();
              },
            )
          }
        >
          {configured ? t('githubSettings.rotate') : t('githubSettings.generate')}
        </Button>
      </div>

      {configured ? <p className="text-xs text-ink-soft">{t('githubSettings.rotateWarning')}</p> : null}
    </div>
  );
}

/**
 * A value the manager has to retype into GitHub, so the copy button is the
 * point rather than a nicety.
 *
 * `navigator.clipboard` is optional-chained: it is undefined on an insecure
 * origin, and a deployment reached over plain HTTP should degrade to "select the
 * text yourself" rather than throwing on click.
 */
function CopyableValue({ label, value }: { label: string; value: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  return (
    <div className="space-y-1.5">
      <p className="text-xs font-semibold text-ink-soft">{label}</p>
      <div className="flex items-center gap-2">
        <code className="grow overflow-x-auto rounded-sm border border-line bg-raised px-3 py-2 font-mono text-xs break-all text-ink">
          {value}
        </code>
        <Button
          variant="secondary"
          icon={copied ? <Check /> : <Copy />}
          onClick={() => {
            void navigator.clipboard?.writeText(value);
            setCopied(true);
          }}
        >
          {copied ? t('common.copied') : t('common.copy')}
        </Button>
      </div>
    </div>
  );
}
